/* eslint-disable max-len */
import {
  Component,
  OnInit,
  Inject,
  ViewChildren,
  QueryList,
  ElementRef,
  ViewChild,
  signal,
  WritableSignal,
  computed,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormArray,
  FormControl,
  UntypedFormArray,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { Observable, filter, switchMap, tap } from 'rxjs';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { MentionConfig } from 'angular-mentions';
import { TsColorOption, TsSnackbarService } from 'tsui';
import { Store } from '@ngxs/store';
import { User } from '@core/models';
import { UserState } from '@state/user/state';
import { RelatedToType } from '@shared/enums';
import { AccountService } from '@core/services';
import { AccountState } from '@state/account/state';
import { FieldDefsState } from '@state/fields/state';
import { FieldDef } from '@core/models/account.types';
import { FeatureFlag } from '@core/enums/feature-flag';
import { MentionsService } from '@shared/services/mentions.service';
import { CustomFieldDialogService } from './custom-field-dialog.service';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { ObjectsManagerActions } from '../../objects-manager/state/actions';
import { FieldUsageResult, FieldsDialogData } from './custom-field-dialog.types';
import * as CustomValidator from '@shared/directives/custom-validators.directive';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { ConfirmService } from '@shared/components/widgets/dialog/confirm/confirm.service';
import { ObjectsManagerService } from '../../objects-manager/services/objects-manager.service';
import { CalcCodeAIAbilityComponent } from '../calc-code-ai-ability/calc-code-ai-ability.component';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { ConditionalFieldStylingFormData } from '../../components/conditional-field-styling-form/conditional-field-styling-form.component';

@Component({
  selector: 'custom-field-dialog',
  templateUrl: './custom-field-dialog.component.html',
  styleUrls: ['./custom-field-dialog.component.scss'],
})
export class CustomFieldDialogComponent extends OnDestroyMixin implements OnInit {
  private initialFormValues: any;
  updateCustomFieldForm: UntypedFormGroup;
  fieldDefs: FieldDef[] = [];
  customObjects: CustomObject[] = [];
  objects: CustomObject[] = [];
  fieldUsageResult: FieldUsageResult;
  loadingFieldUsageResult: boolean;
  user: User;
  displayedColumns: string[] = ['template_name', 'template_type'];
  dataSource = new MatTableDataSource<any>();
  rowDef = ['drag', 'dropdown', 'count', 'actions'];
  mentionConfig: MentionConfig = this.mentionsService.getFieldMentionConfig();
  accountColorOptions$: Observable<TsColorOption[]> = this.store.select(
    AccountState.selectAccountColorsAsPopoverOptions,
  );
  fieldDataWithDropdownOptions: FieldsDialogData;
  conditionalStylingFormData: WritableSignal<ConditionalFieldStylingFormData> = signal({
    conditional_styling: [],
    pristine: true,
    invalid: false,
  });
  conditionalStylingInvalid = computed(() => {
    const { invalid } = this.conditionalStylingFormData();

    return this.data.mode === 'edit' ? invalid : false;
  });

  validFieldTypeChangeMapping = [
    ['money', 'decimal', 'percent'],
    ['number', 'year'],
    ['select', 'text', 'textarea', 'url'],
  ];

  @ViewChildren('inputOptions') inputOptions: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChild('fieldForm', { read: ElementRef }) fieldForm: ElementRef;

  get hasFacilityReportsEnabled$(): Observable<boolean> {
    return this.featureFlagService.hasFeatureFlag(FeatureFlag.FACILITY_REPORTS);
  }

  get hasCustomObjectsEnabled$(): Observable<boolean> {
    return this.featureFlagService.hasFeatureFlag(FeatureFlag.CUSTOM_OBJECTS);
  }

  private get accountId(): number {
    return +this.store.selectSnapshot(AccountState.getAccount).id;
  }

  get optionsFormArray(): FormArray<any> {
    return this.updateCustomFieldForm.get('options') as FormArray;
  }

  get objectType(): string {
    return this.updateCustomFieldForm.get('related_to_type').value;
  }

  get fieldLabel(): string {
    return this.updateCustomFieldForm.get('label').value;
  }

  get dynamicDialogTitle(): string {
    if (this.data.mode === 'add') return 'Add Custom Field';
    if (this.data.fieldType === 'custom') return 'Update Custom Field';
    if (this.data.fieldType === 'standard') return 'Update Standard Field';
  }

  get isStandardField(): boolean {
    return this.data.fieldType === 'standard';
  }

  get isTypeSelectField(): boolean {
    return this.isSelectType || this.updateCustomFieldForm?.get('type')?.value === 'select';
  }

  get options() {
    return this.optionsFormArray.controls;
  }

  get isSelectDropDownListEmpty(): boolean {
    return this.optionsFormArray.controls.length === 0;
  }

  get isFieldTypeDisabled(): boolean {
    if (this.isStandardField) {
      return true;
    } else if (this.fieldUsageResult && this.fieldUsageResult?.values_count > 0) {
      return true;
    } else {
      return false;
    }
  }

  /*
  Determine whether the field type that is passed in and is allowed to be modified.
  */

  public canChangeToFieldType(fieldType): boolean {
    if (!this.data.id) {
      return true;
    } else {
      let foundArray = this.findSubArrayContainingValue(
        this.validFieldTypeChangeMapping,
        this.updateCustomFieldForm.get('type').value,
      );
      if (foundArray) {
        return foundArray.includes(fieldType);
      } else {
        return false;
      }
    }
  }

  private findSubArrayContainingValue(arrays, value) {
    for (let i = 0; i < arrays.length; i++) {
      if (arrays[i].includes(value)) {
        return arrays[i]; // Return the sub-array containing the value
      }
    }
    return null; // Return null if the value isn't found in any sub-array
  }

  private get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  private get isPersistedAsMultiSelect(): boolean {
    return this.data?.meta?.multi_select === true;
  }

  private get isSelectType(): boolean {
    return this.data?.type === 'select';
  }

  /**
   * If field has calc_code, disable readonly checkbox on formBuilder.
   * All fields that has calc_code, are forced by BE to be readonly.
   */
  private get hasCalcCode(): string | null {
    return this.data?.calc_code;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FieldsDialogData,
    private readonly matDialogRef: MatDialogRef<CustomFieldDialogComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly snackBar: TsSnackbarService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly objectsManagerService: ObjectsManagerService,
    private readonly customFieldDialogService: CustomFieldDialogService,
    private readonly confirmDialogService: ConfirmService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly store: Store,
    private readonly dialog: MatDialog,
    private readonly mentionsService: MentionsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.getUser();
    this.getFieldDefs();
    this.getFieldUsage();
    this.buildForm();
    this.initialFormValues = this.updateCustomFieldForm.getRawValue();
    this.optionsFormArrayValueListener();
    this.getCustomObjects();
    this.onDialogBackdropClick();

    this.fieldDataWithDropdownOptions = this.getFieldDataWithDropdownOptions(this.data);
  }

  private buildForm(): void {
    const relatedToTypeValue = this.data.isCustomObject
      ? this.data?.related_to_type?.toLowerCase()
      : this.data?.related_to_type;

    const canToggleMultiSelect =
      !!this.data?.meta?.record_type ||
      this.isStandardField ||
      (this.isEditMode && this.isSelectType && this.isPersistedAsMultiSelect);

    this.updateCustomFieldForm = this.formBuilder.group({
      related_to_type: [
        {
          /**
           * If dialog is opened from the import stepper under settings, we have to disable the select and also set its value according to the object type user has chosen in step 1.
           * That way we ensure that the user won't create a field in a wrong object-type context.
           */
          value: this.relatedToType(this.data.fromImport ? this.data.importObjectType : relatedToTypeValue),
          disabled: this.data?.id || this.isStandardField || this.data.fromImport,
        },
        [Validators.required],
      ],
      label: [
        { value: this.data?.label || '', disabled: this.isStandardField },
        [
          Validators.required,
          CustomValidator.uniqueNameValidator(
            this.fieldDefs.filter(
              (fieldDef) =>
                fieldDef.related_to_type ===
                  this.relatedToType(this.data.fromImport ? this.data.importObjectType : relatedToTypeValue) &&
                fieldDef.label !== this.data?.label,
            ),
            'label',
          ),
        ],
      ],
      type: [
        { value: this.data?.type ? String(this.data.type) : '', disabled: this.isFieldTypeDisabled },
        [Validators.required],
      ],
      record_type: [{ value: this.data?.meta?.record_type || '', disabled: this.data?.id || this.isStandardField }],
      options: this.setDropDownOptions(),
      multi_select: [
        {
          value: this.data?.meta?.multi_select || false,
          disabled: canToggleMultiSelect,
        },
      ],
      user_editable: [{ value: this.data?.meta?.user_editable || false, disabled: this.isStandardField }],
      scale: [{ value: this.data?.meta?.scale || 0, disabled: this.isStandardField }],
      description: [{ value: this.data?.description || '', disabled: this.isStandardField }],
      archived: [this.data?.archived || false],
      readonly: [{ value: this.data?.meta?.readonly || false, disabled: this.hasCalcCode }],
      show_history: [this.data?.show_history || false],
      inherited: [this.data?.inherited || false],
      default_calc_code: [{ value: this.data?.meta?.default_calc_code || '', disabled: this.isStandardField }],
      calc_code: [
        { value: this.data?.meta?.calc_code || this.data?.meta?.calc_description, disabled: this.isStandardField },
      ],
      calc_index: [{ value: this.data?.meta?.calc_index, disabled: this.isStandardField }],
    });

    this.dataSource.data = [...this.options];
  }

  private hasFormChanges(): boolean {
    const currentValues = this.updateCustomFieldForm.getRawValue();
    return JSON.stringify(currentValues) !== JSON.stringify(this.initialFormValues);
  }

  private setDropDownOptions(): UntypedFormArray {
    if (this.data.options) {
      return this.formBuilder.array(
        this.data.options?.map((x: string) => new FormControl({ value: x, disabled: this.isStandardField })),
      );
    } else {
      return this.formBuilder.array([]);
    }
  }

  private getFieldDefs(): void {
    this.fieldDefs = this.store.selectSnapshot(FieldDefsState.getFieldDefs);
    this.mentionConfig.mentions[1].items = this.fieldDefs as any;
  }

  private getFieldUsage(): void {
    if (!this.data?.name) {
      this.fieldUsageResult = {} as FieldUsageResult;
      this.loadingFieldUsageResult = false;
      return;
    }

    this.loadingFieldUsageResult = true;
    this.customFieldDialogService
      .getUsage(this.data.name, this.data.related_to_type)
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        this.fieldUsageResult = result;
        this.loadingFieldUsageResult = false;
        //[sc-26834] - we have to call this again now that we have the results, so we can disable the field type select if it's being used.
        if (this.isFieldTypeDisabled) {
          this.updateCustomFieldForm.get('type').disable();
        }
      });
  }

  private optionsFormArrayValueListener(): void {
    this.optionsFormArray.valueChanges.pipe(untilComponentDestroyed(this)).subscribe((value) => {
      if (value) this.optionsFormArray.markAsDirty();
    });
  }

  getUsageFieldsValue(value: string): number {
    const valueCount = this.fieldUsageResult?.option_values_count;
    return valueCount?.find((x) => Object.keys(x).includes(value))?.[value] || 0;
  }

  /**
   * Set 'isNewOption' property to true for each new option added to the dropdown so we can differentiate them from the existing ones. Existing ones can't be edited.
   */
  addDropDownValue(): void {
    const newControl = new FormControl({ value: '', disabled: this.isStandardField });
    newControl['isNewOption'] = true;
    this.optionsFormArray.push(newControl);
    this.dataSource.data = [...this.options];
  }

  editDropDownValue(index: number): void {
    if (index >= 0 && index < this.inputOptions.length) {
      const inputRef = this.inputOptions.toArray()[index];
      inputRef.nativeElement.classList.remove('not-allowed');
      inputRef.nativeElement.readOnly = false;
      inputRef.nativeElement.focus();
      inputRef.nativeElement.select();
    }
  }

  removeDropDownValue(index: number): void {
    const option = this.optionsFormArray.at(index).value;
    this.confirmDialogService
      .openConfirmationModal({
        title: 'Delete Confirmation',
        message: `Are you sure you want to delete the dropdown value <code>${option}</code> ?`,
      })
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
      )
      .subscribe(() => this.removeOptionByIndex(index));
  }

  private removeOptionByIndex(index: number): void {
    this.optionsFormArray.removeAt(index);
    this.dataSource.data = [...this.options];
  }

  private isOptionUsed(option: string): boolean {
    return this.getUsageFieldsValue(option) > 0;
  }

  canEditOrDeleteDropDownOption(index: number): boolean {
    return !this.isOptionUsed(this.optionsFormArray.at(index).value);
  }

  drop(event): void {
    moveItemInArray(this.optionsFormArray.controls, event.previousIndex, event.currentIndex);
    this.optionsFormArray.markAsDirty();
  }

  private handleSaveProcess(observable: Observable<any>, successMessage: string): void {
    observable.pipe(untilComponentDestroyed(this)).subscribe({
      next: (result) => {
        this.snackBar.open(successMessage, 'success');
        this.accountService.fieldDefsCacheBuster();
        this.matDialogRef.close(result);
      },
      error: (err: HttpErrorResponse) => {
        const errorMessage = err?.error?.custom_field?.errors
          ? err?.error?.custom_field?.errors
          : 'An error occurred. Please try again.';
        this.snackBar.open(errorMessage, 'error');
        this.matDialogRef.close();
      },
    });
  }

  private onDialogBackdropClick(): void {
    this.matDialogRef
      .backdropClick()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => this.discard());
  }

  save(): void {
    this.updateCustomFieldForm.disable();

    const type = this.updateCustomFieldForm.get('type')?.value;

    const baseParams = {
      ...this.updateCustomFieldForm.value,
      options: type === 'select' ? this.optionsFormArray.getRawValue() : '',
    };

    let observable: Observable<any>;
    let successMessage = '';

    if (this.data.mode === 'add') {
      observable = this.accountService.createCustomField(baseParams);
      successMessage = 'Custom field successfully created';
    } else {
      [observable, successMessage] = this.handleEditAndLockModes(baseParams);
    }

    observable = observable.pipe(
      tap((field) => this.store.dispatch(new ObjectsManagerActions.AddOrUpdateObjectField(field))),
    );

    this.handleSaveProcess(observable, successMessage);
  }

  handleEditAndLockModes(baseParams: any): [observable: Observable<any>, successMessage: string] {
    let observable: Observable<any>;
    let successMessage = '';

    const { conditional_styling } = this.conditionalStylingFormData();
    const newBaseParams = {
      ...baseParams,
      ...(conditional_styling.length > 0 ? { conditional_styling } : { conditional_styling: [] }),
    };

    if (this.data.fieldType === 'custom') {
      newBaseParams.id = this.data?.id;
      observable = this.accountService.updateCustomField(newBaseParams);
      successMessage = 'Custom field successfully updated';
    } else if (this.data.fieldType === 'standard') {
      newBaseParams.name = this.data.name;
      observable = this.accountService.updateStandardField(newBaseParams);
      successMessage = 'Standard field successfully updated';
    }

    return [observable, successMessage];
  }

  discard(): void {
    if (this.hasFormChanges()) {
      this.confirmationDialogService
        .openConfirmationDialog({
          dialogTitle: 'You have unsaved changes',
          description: 'All changes will be discarded.',
          buttonsConfig: {
            yes: 'Keep Changes',
            no: 'Discard',
          },
        })
        .pipe(untilComponentDestroyed(this))
        .subscribe((result) => {
          if (!result) {
            this.matDialogRef.close();
          }
        });
    } else {
      this.matDialogRef.close();
    }
  }

  trackByFn(index: number, item: any): number {
    return item.id || index;
  }

  enablePrecision(): boolean {
    const currentFieldType = this.updateCustomFieldForm.getRawValue().type;
    const scaleFieldTypes = ['decimal', 'number', 'money', 'percent'];
    return scaleFieldTypes.includes(currentFieldType);
  }

  isMoney(): boolean {
    const currentFieldType = this.updateCustomFieldForm.getRawValue().type;
    return currentFieldType === 'money';
  }

  private relatedToType(type: string): string {
    switch (type) {
      case 'Deal':
        return RelatedToType.Project;
      case 'Lease':
        return RelatedToType.PropertyTenant;
      case 'Unit':
        return RelatedToType.PropertyUnit;
      default:
        return type;
    }
  }

  private getCustomObjects(): void {
    this.hasCustomObjectsEnabled$
      .pipe(
        untilComponentDestroyed(this),
        filter((hasCustomObjectsEnabled) => hasCustomObjectsEnabled),
        switchMap(() => this.objectsManagerService.get(this.accountId)),
      )
      .subscribe((objects) => {
        this.objects = objects;
        this.customObjects = objects.filter((object) => object?.id);
      });
  }

  getUser(): void {
    this.user = this.store.selectSnapshot(UserState.getUser);
  }

  openCalcCodeAIAbilityDialog(): void {
    // This map is used to transform the fieldDefs to the format that the AI ability expects
    // This is also important to avoid sending unnecessary data to the AI ability that relies on LLM context window size
    const listOfFields = this.fieldDefs
      .filter((field) => field.related_to_type === this.updateCustomFieldForm.get('related_to_type').value)
      .map((field) => ({
        name: field.name,
        label: field.label,
        type: field.type,
        related_to_type: field.related_to_type,
      }));

    const dialogRef = this.dialog.open(CalcCodeAIAbilityComponent, {
      height: '450px',
      width: '600px',
      data: {
        listOfFields,
        fieldLabel: this.updateCustomFieldForm.get('label').value,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result) {
          this.updateCustomFieldForm.get('calc_code').setValue(result);
          this.updateCustomFieldForm.get('calc_code').markAsDirty();
        }
      });
  }

  private getFieldDataWithDropdownOptions(data: FieldsDialogData) {
    const hasOptionName = !!data?.meta?.option_name;
    const hasOptions = data?.meta?.options && data?.meta?.options.length > 0;

    let options = data?.meta?.options ?? [];

    if (hasOptionName && !hasOptions) {
      options = this.store
        .selectSnapshot(FieldDefsState.selectDropdownOptionsBy(data?.meta?.option_name))
        .map((option) => ({ label: option.name, value: option.id }));
    }

    return {
      ...data,
      meta: {
        ...data.meta,
        options,
      },
    };
  }

  protected onStylingConditionAdded(): void {
    setTimeout(() => {
      this.fieldForm?.nativeElement?.scrollTo({
        top: this.fieldForm?.nativeElement?.scrollHeight,
        behavior: 'smooth',
      });
    });
  }

  onConditionalStylingChanged($event: ConditionalFieldStylingFormData) {
    this.updateCustomFieldForm.markAsDirty();

    this.conditionalStylingFormData.set($event);
  }
}
